import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getHelpBotResponse, detectContext } from '../services/helpBotService';
import './HelpBot.css';

function HelpBot({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const location = useLocation();
  
  const context = detectContext(location.pathname);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: `Hi! I'm your Desk Suite assistant. I can help you with:

• How to use the Defect Tracker
• Creating and managing Feature Requests
• Working with Notes
• Team messaging
• General navigation

What would you like help with?`,
          timestamp: new Date()
        }
      ]);
    }
  }, []);

  const handleSend = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputText,
      timestamp: new Date(),
      context
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      const response = await getHelpBotResponse(
        [...messages, userMessage],
        context,
        user
      );

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Help bot error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or check that your API key is configured.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <button
        className={`help-bot-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Help Assistant"
      >
        {isOpen ? '✕' : '💬'}
      </button>

      {isOpen && (
        <div className="help-bot-widget">
          <div className="help-bot-header">
            <div className="help-bot-title">
              <span className="help-bot-icon">🤖</span>
              <div>
                <h3>Help Assistant</h3>
                <span className="help-bot-context">{context}</span>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="help-bot-close">✕</button>
          </div>

          <div className="help-bot-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`help-bot-message ${msg.role}`}>
                <div className="message-avatar">
                  {msg.role === 'user' ? '👤' : '🤖'}
                </div>
                <div className="message-content">
                  <div className="message-text">{msg.content}</div>
                  <div className="message-time">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="help-bot-message assistant">
                <div className="message-avatar">🤖</div>
                <div className="message-content">
                  <div className="message-loading">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="help-bot-input">
            <textarea
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask a question..."
              rows="2"
              disabled={isLoading}
            />
            <button 
              onClick={handleSend}
              disabled={!inputText.trim() || isLoading}
              className="help-bot-send"
            >
              {isLoading ? '...' : '➤'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default HelpBot;
